import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldAlert, UserPlus, Trash2, Power, History, Users, Upload, FileSpreadsheet, Eraser, BarChart3 } from "lucide-react";

const ACTION_STYLE = {
  LOGIN: "bg-blue-50 text-blue-700 border-blue-200",
  LOGOUT: "bg-slate-100 text-slate-600 border-slate-200",
  CREATE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  UPDATE: "bg-amber-50 text-amber-800 border-amber-200",
  DELETE: "bg-red-50 text-red-700 border-red-200",
  MASTER_ACCESS: "bg-indigo-50 text-indigo-700 border-indigo-200",
  MASTER_DENIED: "bg-red-50 text-red-700 border-red-200",
};

const fmt = (v) => (v ? new Date(v).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "—");

export const MasterPanel = ({ open, onOpenChange }) => {
  const [unlocked, setUnlocked] = useState(Boolean(sessionStorage.getItem("es_master_token")));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [logQuery, setLogQuery] = useState("");
  const [logAction, setLogAction] = useState("");
  const [nu, setNu] = useState({ username: "", name: "", password: "", role: "user" });
  const [importing, setImporting] = useState(false);
  const [importResumo, setImportResumo] = useState(null);
  const [dataCorte, setDataCorte] = useState("");
  const [produtividade, setProdutividade] = useState(null);
  const hojeData = new Date();
  const [prodAno, setProdAno] = useState(
    new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric" }).format(hojeData),
  );
  const [prodMes, setProdMes] = useState(
    String(Number(new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", month: "numeric" }).format(hojeData))),
  );

  const [prodDia, setProdDia] = useState("");
  const [prodVisao, setProdVisao] = useState("usuario");

  const loadProdutividade = useCallback(async () => {
    try {
      const { data } = await api.get("/stats/usuarios", {
        params: prodDia
          ? { dia: prodDia }
          : {
              ano: prodAno ? Number(prodAno) : undefined,
              mes: prodAno && prodMes ? Number(prodMes) : undefined,
            },
      });
      setProdutividade(data);
    } catch {
      setProdutividade(null);
    }
  }, [prodAno, prodMes, prodDia]);

  const limparAuditoria = async (antesDe) => {
    const msg = antesDe
      ? `Excluir os registros de auditoria anteriores a ${antesDe.split("-").reverse().join("/")}?`
      : "Excluir TODO o histórico de rastreio/auditoria? Esta ação não pode ser desfeita.";
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete("/audit", {
        params: antesDe ? { antes_de: antesDe } : undefined,
      });
      toast.success(`${data.excluidos} registro(s) de auditoria excluído(s)`);
      setDataCorte("");
      loadAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const importPlanilha = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setImportResumo(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/planilha/import", fd);
      setImportResumo(data);
      toast.success(`Planilha importada: ${data.inseridos} novo(s), ${data.atualizados} atualizado(s)`);
      loadAll();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setImporting(false);
    }
  };

  const loadAll = useCallback(async () => {
    try {
      const [u, l, s] = await Promise.all([
        api.get("/users"),
        api.get("/audit", { params: { limit: 100, q: logQuery || undefined, action: logAction || undefined } }),
        api.get("/audit/summary"),
      ]);
      setUsers(u.data);
      setLogs(l.data.items);
      setSummary(s.data);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        sessionStorage.removeItem("es_master_token");
        setUnlocked(false);
      }
    }
  }, [logQuery, logAction]);

  useEffect(() => {
    if (open && unlocked) loadAll();
  }, [open, unlocked, loadAll]);

  useEffect(() => {
    if (open && unlocked) loadProdutividade();
  }, [open, unlocked, loadProdutividade]);

  const unlock = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/master/unlock", { password });
      sessionStorage.setItem("es_master_token", data.master_token);
      setPassword("");
      setUnlocked(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", nu);
      toast.success("Usuário cadastrado");
      setNu({ username: "", name: "", password: "", role: "user" });
      loadAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u._id}`, { active: !(u.active ?? true) });
      loadAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const resetPass = async (u) => {
    const p = window.prompt(`Nova senha para "${u.username}":`);
    if (!p) return;
    try {
      await api.put(`/users/${u._id}`, { password: p });
      toast.success("Senha atualizada");
      loadAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const delUser = async (u) => {
    if (!window.confirm(`Excluir o usuário "${u.username}"?`)) return;
    try {
      await api.delete(`/users/${u._id}`);
      toast.success("Usuário excluído");
      loadAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={unlocked ? "max-w-6xl w-[96vw] max-h-[92vh] overflow-hidden p-0" : "max-w-md"}
        data-testid="master-panel"
      >
        {!unlocked ? (
          <form onSubmit={unlock} data-testid="master-unlock-form">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-display">
                <ShieldAlert className="h-5 w-5 text-primary" /> Painel do Administrador
              </DialogTitle>
              <DialogDescription className="text-sm">
                Área restrita. Informe a senha do administrador master.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Senha do administrador</Label>
              <Input
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="master-password-input"
                className="mt-1.5 h-11"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="master-error" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy} data-testid="master-unlock-btn" className="mt-5 w-full h-11 bg-primary hover:bg-[#003366]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Desbloquear
            </Button>
          </form>
        ) : (
          <div className="flex flex-col max-h-[92vh]">
            <DialogHeader className="px-6 pt-6 pb-4 border-b">
              <DialogTitle className="flex items-center gap-2 font-display tracking-tight">
                <ShieldAlert className="h-5 w-5 text-primary" /> Painel do Administrador
              </DialogTitle>
              <DialogDescription className="text-xs">
                Gestão de usuários e rastreio de acessos e modificações.
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="audit" className="flex-1 overflow-hidden flex flex-col">
              <div className="px-6 pt-4">
                <TabsList>
                  <TabsTrigger value="audit" data-testid="tab-audit">
                    <History className="h-4 w-4 mr-2" /> Rastreio / Auditoria
                  </TabsTrigger>
                  <TabsTrigger value="users" data-testid="tab-users">
                    <Users className="h-4 w-4 mr-2" /> Usuários
                  </TabsTrigger>
                  <TabsTrigger value="import" data-testid="tab-import">
                    <FileSpreadsheet className="h-4 w-4 mr-2" /> Importar planilha
                  </TabsTrigger>
                  <TabsTrigger value="produtividade" data-testid="tab-produtividade">
                    <BarChart3 className="h-4 w-4 mr-2" /> Tramitações por usuário
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="audit" className="flex-1 overflow-auto px-6 pb-6 mt-4">
                {summary && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    {[
                      ["Acessos registrados", summary.logins],
                      ["Modificações", summary.changes],
                      ["Usuários", summary.users],
                    ].map(([l, v]) => (
                      <div key={l} className="border border-border bg-white p-4">
                        <p className="text-[11px] uppercase tracking-wider text-slate-500">{l}</p>
                        <p className="text-2xl font-semibold font-display mt-1">{v}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mb-3">
                  <Input
                    placeholder="Buscar por usuário, ação ou descrição…"
                    value={logQuery}
                    onChange={(e) => setLogQuery(e.target.value)}
                    data-testid="audit-search"
                    className="h-9 max-w-xs text-sm"
                  />
                  <select
                    value={logAction}
                    onChange={(e) => setLogAction(e.target.value)}
                    data-testid="audit-action-filter"
                    className="h-9 border border-input bg-white px-3 text-sm rounded-md"
                  >
                    <option value="">Todas as ações</option>
                    {(summary?.actions || []).map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                  <div className="flex flex-wrap items-center gap-2 ml-auto">
                    <div className="flex items-center gap-2 border border-border px-2 py-1 rounded-md bg-slate-50">
                      <span className="text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">
                        Anteriores a
                      </span>
                      <Input
                        type="date"
                        value={dataCorte}
                        onChange={(e) => setDataCorte(e.target.value)}
                        data-testid="audit-data-corte"
                        className="h-7 w-[140px] text-xs border-0 bg-transparent px-0 focus-visible:ring-0"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!dataCorte}
                        onClick={() => limparAuditoria(dataCorte)}
                        data-testid="limpar-auditoria-parcial-btn"
                        className="h-7 text-xs text-amber-800 border-amber-200 hover:bg-amber-50"
                      >
                        <Eraser className="h-3.5 w-3.5 mr-1.5" /> Limpar até a data
                      </Button>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => limparAuditoria(null)}
                      data-testid="limpar-auditoria-btn"
                      className="h-9 text-red-700 border-red-200 hover:bg-red-50"
                    >
                      <Eraser className="h-4 w-4 mr-2" /> Limpar tudo
                    </Button>
                  </div>
                </div>

                <div className="border border-border">
                  <table className="w-full text-sm" data-testid="audit-table">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-2">Data/Hora</th>
                        <th className="px-3 py-2">Usuário</th>
                        <th className="px-3 py-2">Ação</th>
                        <th className="px-3 py-2">Descrição</th>
                        <th className="px-3 py-2">Modificações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((l) => (
                        <tr key={l._id} className="border-t border-border align-top hover:bg-slate-50" data-testid={`audit-row-${l._id}`}>
                          <td className="px-3 py-2 mono text-xs whitespace-nowrap">{fmt(l.created_at)}</td>
                          <td className="px-3 py-2">
                            <div className="font-medium">{l.name || l.username}</div>
                            <div className="text-xs text-slate-500 mono">{l.username}</div>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={ACTION_STYLE[l.action] || ""}>{l.action}</Badge>
                          </td>
                          <td className="px-3 py-2">{l.description}</td>
                          <td className="px-3 py-2">
                            {(l.changes || []).length === 0 ? (
                              <span className="text-slate-400">—</span>
                            ) : (
                              <ul className="space-y-0.5 text-xs">
                                {l.changes.slice(0, 6).map((c, i) => (
                                  <li key={i} className="mono">
                                    <span className="text-slate-500">{c.campo}:</span>{" "}
                                    <span className="text-red-700 line-through">{String(c.de ?? "—")}</span>{" → "}
                                    <span className="text-emerald-700">{String(c.para ?? "—")}</span>
                                  </li>
                                ))}
                                {l.changes.length > 6 && (
                                  <li className="text-slate-400">+{l.changes.length - 6} campos</li>
                                )}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                      {logs.length === 0 && (
                        <tr><td colSpan={5} className="px-3 py-10 text-center text-slate-400">Nenhum registro de auditoria</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="users" className="flex-1 overflow-auto px-6 pb-6 mt-4">
                <form onSubmit={createUser} className="border border-border bg-slate-50 p-4 mb-5" data-testid="create-user-form">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Cadastrar novo usuário</p>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
                    <div>
                      <Label className="text-xs">Usuário</Label>
                      <Input value={nu.username} onChange={(e) => setNu({ ...nu, username: e.target.value })}
                        data-testid="new-user-username" className="h-9 mono text-sm" required />
                    </div>
                    <div>
                      <Label className="text-xs">Nome</Label>
                      <Input value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })}
                        data-testid="new-user-name" className="h-9 text-sm" required />
                    </div>
                    <div>
                      <Label className="text-xs">Senha</Label>
                      <Input value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })}
                        data-testid="new-user-password" className="h-9 text-sm" required />
                    </div>
                    <div>
                      <Label className="text-xs">Perfil</Label>
                      <select value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })}
                        data-testid="new-user-role"
                        className="h-9 w-full border border-input bg-white px-2 text-sm rounded-md">
                        <option value="user">Usuário</option>
                        <option value="master">Master</option>
                      </select>
                    </div>
                    <Button type="submit" data-testid="create-user-btn" className="h-9 bg-primary hover:bg-[#003366]">
                      <UserPlus className="h-4 w-4 mr-2" /> Cadastrar
                    </Button>
                  </div>
                </form>

                <div className="border border-border">
                  <table className="w-full text-sm" data-testid="users-table">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-2">Usuário</th>
                        <th className="px-3 py-2">Nome</th>
                        <th className="px-3 py-2">Perfil</th>
                        <th className="px-3 py-2">Situação</th>
                        <th className="px-3 py-2">Último acesso</th>
                        <th className="px-3 py-2 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u._id} className="border-t border-border hover:bg-slate-50" data-testid={`user-row-${u.username}`}>
                          <td className="px-3 py-2 mono">{u.username}</td>
                          <td className="px-3 py-2">{u.name}</td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={u.role === "master" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : ""}>
                              {u.role === "master" ? "MASTER" : "USUÁRIO"}
                            </Badge>
                          </td>
                          <td className="px-3 py-2">
                            {u.active ?? true ? (
                              <span className="text-emerald-700">Ativo</span>
                            ) : (
                              <span className="text-red-700">Inativo</span>
                            )}
                          </td>
                          <td className="px-3 py-2 mono text-xs">{fmt(u.last_login)}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={() => resetPass(u)} data-testid={`reset-pass-${u.username}`}>
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.username}`}>
                              <Power className="h-4 w-4" />
                            </Button>
                            {u.role !== "master" && (
                              <Button size="sm" variant="ghost" onClick={() => delUser(u)} data-testid={`delete-user-${u.username}`}>
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="import" className="flex-1 overflow-auto px-6 pb-6 mt-4">
                <div className="border border-border bg-white p-6 max-w-2xl">
                  <div className="flex items-start gap-3">
                    <FileSpreadsheet className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium font-display">Subir nova versão da planilha</p>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                        Envie o arquivo <span className="mono">.xlsx</span> com as abas{" "}
                        <span className="mono">PROCESSOS</span> e <span className="mono">SINDICÂNCIA</span>.
                        Os registros existentes (mesmo nº de processo e ano) são{" "}
                        <strong>atualizados</strong> e os novos são incluídos — nada é apagado e todo o
                        histórico de auditoria é preservado.
                      </p>
                    </div>
                  </div>

                  <label className="mt-5 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border hover:border-primary hover:bg-slate-50 transition-colors duration-150 py-10 cursor-pointer">
                    {importing ? (
                      <>
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        <span className="text-sm text-slate-500">Importando… isso pode levar alguns minutos</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-slate-400" />
                        <span className="text-sm text-slate-600">Clique para escolher a planilha (.xlsx)</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept=".xlsx,.xlsm"
                      className="hidden"
                      disabled={importing}
                      onChange={importPlanilha}
                      data-testid="import-planilha-input"
                    />
                  </label>

                  {importResumo && (
                    <div className="mt-5 border border-emerald-200 bg-emerald-50 p-4" data-testid="import-resumo">
                      <p className="text-sm font-medium text-emerald-900">
                        Importação concluída · {importResumo.arquivo}
                      </p>
                      <div className="grid grid-cols-4 gap-3 mt-3">
                        {[
                          ["Lidos", importResumo.lidos],
                          ["Novos", importResumo.inseridos],
                          ["Atualizados", importResumo.atualizados],
                          ["Sem alteração", importResumo.inalterados],
                        ].map(([l, v]) => (
                          <div key={l}>
                            <p className="text-[10px] uppercase tracking-wider text-emerald-700">{l}</p>
                            <p className="text-xl font-semibold font-display text-emerald-900 mono">{v}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="produtividade" className="flex-1 overflow-auto px-6 pb-6 mt-4">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <div className="flex border border-border bg-white">
                    {[["usuario", "Por usuário"], ["dia", "Por dia"]].map(([v, l]) => (
                      <button
                        key={v}
                        onClick={() => setProdVisao(v)}
                        data-testid={`prod-visao-${v}`}
                        className={`px-3 h-8 text-xs transition-colors duration-150 ${
                          prodVisao === v ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <Input
                      type="date"
                      value={prodDia}
                      onChange={(e) => setProdDia(e.target.value)}
                      data-testid="prod-dia-filter"
                      className="h-8 w-[150px] text-xs"
                    />
                    {prodDia && (
                      <button
                        onClick={() => setProdDia("")}
                        data-testid="prod-dia-limpar"
                        className="text-xs text-slate-500 hover:text-primary underline"
                      >
                        limpar dia
                      </button>
                    )}
                    <select
                      value={prodMes}
                      onChange={(e) => setProdMes(e.target.value)}
                      disabled={!prodAno || Boolean(prodDia)}
                      data-testid="prod-mes-filter"
                      className="h-8 border border-input bg-white px-2 text-xs rounded-md disabled:opacity-40"
                    >
                      {["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho",
                        "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"].map((m, i) => (
                        <option key={m} value={i + 1}>{m}</option>
                      ))}
                    </select>
                    <select
                      value={prodAno}
                      onChange={(e) => setProdAno(e.target.value)}
                      disabled={Boolean(prodDia)}
                      data-testid="prod-ano-filter"
                      className="h-8 border border-input bg-white px-2 text-xs rounded-md mono disabled:opacity-40"
                    >
                      <option value="">Todo o período</option>
                      {[0, 1, 2].map((d) => {
                        const a = hojeData.getFullYear() - d;
                        return <option key={a} value={a}>{a}</option>;
                      })}
                    </select>
                  </div>
                </div>

                {produtividade && (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {[
                        ["Tramitações", produtividade.total_tramitados],
                        ["Cadastros", produtividade.total_cadastrados],
                        ["Total de ações", produtividade.total_geral],
                      ].map(([l, v]) => (
                        <div key={l} className="border border-border bg-white p-4">
                          <p className="text-[11px] uppercase tracking-wider text-slate-500">{l}</p>
                          <p className="text-2xl font-semibold font-display mono mt-1">
                            {v.toLocaleString("pt-BR")}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="border border-border">
                      {prodVisao === "usuario" ? (
                      <table className="w-full text-sm" data-testid="produtividade-table">
                        <thead className="bg-slate-50">
                          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                            <th className="px-3 py-2">Usuário</th>
                            <th className="px-3 py-2 text-right">Tramitados</th>
                            <th className="px-3 py-2 text-right">Cadastrados</th>
                            <th className="px-3 py-2 text-right">Trocas de setor</th>
                            <th className="px-3 py-2 text-right">Excluídos</th>
                            <th className="px-3 py-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {produtividade.itens.map((u) => (
                            <tr key={u.username} className="border-t border-border hover:bg-slate-50"
                              data-testid={`produtividade-row-${u.username}`}>
                              <td className="px-3 py-2">
                                <div className="font-medium">{u.nome}</div>
                                <div className="text-xs text-slate-500 mono">{u.username}</div>
                              </td>
                              <td className="px-3 py-2 text-right mono">{u.tramitados.toLocaleString("pt-BR")}</td>
                              <td className="px-3 py-2 text-right mono text-emerald-700">{u.cadastrados.toLocaleString("pt-BR")}</td>
                              <td className="px-3 py-2 text-right mono text-amber-700">{u.trocas_setor.toLocaleString("pt-BR")}</td>
                              <td className="px-3 py-2 text-right mono text-red-700">{u.excluidos.toLocaleString("pt-BR")}</td>
                              <td className="px-3 py-2 text-right mono font-semibold">{u.total.toLocaleString("pt-BR")}</td>
                            </tr>
                          ))}
                          {produtividade.itens.length === 0 && (
                            <tr>
                              <td colSpan={6} className="px-3 py-10 text-center text-slate-400" data-testid="produtividade-vazio">
                                Nenhuma tramitação registrada no período
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      ) : (
                      <table className="w-full text-sm" data-testid="produtividade-dia-table">
                        <thead className="bg-slate-50">
                          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                            <th className="px-3 py-2">Dia</th>
                            <th className="px-3 py-2">Usuários</th>
                            <th className="px-3 py-2 text-right">Tramitados</th>
                            <th className="px-3 py-2 text-right">Cadastrados</th>
                            <th className="px-3 py-2 text-right">Trocas de setor</th>
                            <th className="px-3 py-2 text-right">Excluídos</th>
                            <th className="px-3 py-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(produtividade.por_dia || []).map((d) => (
                            <tr key={d.data} className="border-t border-border hover:bg-slate-50 align-top"
                              data-testid={`produtividade-dia-row-${d.data}`}>
                              <td className="px-3 py-2 mono whitespace-nowrap font-medium">
                                {d.data.split("-").reverse().join("/")}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap gap-x-3 gap-y-1">
                                  {d.usuarios.map((u) => (
                                    <span key={u.username} className="text-xs text-slate-600">
                                      {u.nome}
                                      <span className="mono text-slate-900 ml-1">{u.total}</span>
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right mono">{d.tramitados.toLocaleString("pt-BR")}</td>
                              <td className="px-3 py-2 text-right mono text-emerald-700">{d.cadastrados.toLocaleString("pt-BR")}</td>
                              <td className="px-3 py-2 text-right mono text-amber-700">{d.trocas_setor.toLocaleString("pt-BR")}</td>
                              <td className="px-3 py-2 text-right mono text-red-700">{d.excluidos.toLocaleString("pt-BR")}</td>
                              <td className="px-3 py-2 text-right mono font-semibold">{d.total.toLocaleString("pt-BR")}</td>
                            </tr>
                          ))}
                          {(produtividade.por_dia || []).length === 0 && (
                            <tr>
                              <td colSpan={7} className="px-3 py-10 text-center text-slate-400" data-testid="produtividade-dia-vazio">
                                Nenhuma tramitação registrada no período
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      )}
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
